//
//
//
//

import { stripHtml, truncateString } from '@motionelements/core/src/helpers/string.js';

import { fromUnixTime, formatISO } from 'date-fns';
import { isProductType } from '@motionelements/core/src/services/catalog.service.js';

export default {
  name: 'base-page-meta-schema',
  props: {
    breadcrumbs: {
      type: Array,
      required: false,
    },
    product: {
      type: Object,
      required: true,
    },
    mediatype: String,
    mediaTypeGroup: String,
    title: String,
    meta: {
      type: Array,
      default: () => [],
    },
    description: String,
  },
  head() {
    const scopeMeta = [
      { hid: 'description', name: 'description', content: this.pageDescription },
      { hid: 'og:description', property: 'og:description', content: this.pageDescription },
      { hid: 'og:image', property: 'og:image', content: this.ogImage },
      { hid: 'og:locale', property: 'og:locale', content: this.ogLang },
      { hid: 'og:title', property: 'og:title', content: this.pageTitle },
      { hid: 'og:url', property: 'og:url', content: this.canonicalUrl },
      { hid: 'robots', name: 'robots', content: this.robot },
      { hid: 'twitter:description', name: 'twitter:description', content: this.pageDescription },
      { hid: 'twitter:title', name: 'twitter:title', content: this.pageTitle },
      { hid: 'twitter:url', name: 'twitter:url', content: this.canonicalUrl },
      { hid: 'twitter:creator', name: 'twitter:creator', content: '@MotionElements' },
      { hid: 'twitter:image:src', name: 'twitter:image:src', content: this.ogImage },
    ];

    // replace, add other meta passed in
    this.meta.forEach((metaItem) => {
      const index = scopeMeta.findIndex(m => m.hid === metaItem.hid);
      if (index >= 0) {
        scopeMeta[index] = metaItem;
      } else {
        scopeMeta.push(metaItem);
      }
    });

    return {
      // titleTemplate: '%s | MotionElements',
      title: this.pageTitle,
      link: this.getPageLangLink(),
      meta: scopeMeta,
      script: this.pageJsonLds,
    };
  },
  computed: {
    isElement() {
      return this.product.object === 'element';
    },
    isCollection() {
      return this.product.object === 'collection';
    },
    contributorName() {
      return _.get(this.product, 'artist.username');
    },
    pageTitle() {
      return _.get(this.product, 'html.title', this.product.id);
    },
    pageDescription() {
      return _.get(this.product, 'html.description', this.product.id);
    },
    descriptionLength() {
      const descriptions = _.get(this.product, 'descriptions');

      if (Array.isArray(descriptions) && descriptions.length) {
        let length = 0;

        descriptions.forEach((obj) => {
          const html = _.get(obj, 'html');
          if (html && html.length > length) {
            length = obj.html.length;
          }
        });

        return length;
      }

      return _.get(this.product, 'description', '').length;
    },
    robot() {
      if (process.env.VUE_APP_ENV !== 'production') {
        return 'none';
      }

      let robotContent = 'none';

      const hasEnoughDescription = this.descriptionLength > 190;
      const isAudioWithoutAlbumArt = isProductType('audio', this.mediaType) && !_.get(this.product, 'albumArt.jpg.url');

      if (hasEnoughDescription) {
        robotContent = 'index, nofollow';
        if (isAudioWithoutAlbumArt) {
          robotContent += ', noimageindex'; // don't index waveform image
        }
      }
      return robotContent;
    },
    mediaType() {
      if (this.isCollection) {
        return null;
      }

      return _.get(this.product, 'mediaType');
    },
    masterVariant() {
      return _.get(this.product, 'variants', []).find(v => v.master === true);
    },
    width() {
      return _.get(this.masterVariant, 'width', null);
    },
    height() {
      return _.get(this.masterVariant, 'height', null);
    },
    ogLang() {
      switch (this.$i18n.locale) {
        case 'zh-hant':
          return 'zh_TW';
        case 'zh-hans':
          return 'zh_CN';
        case 'ja':
          return 'ja_JP';
        case 'es':
          return 'es_ES';
        case 'th':
          return 'th_TH';
        case 'ko':
          return 'ko_KR';
        case 'de':
          return 'de_DE';
        case 'fr':
          return 'fr_FR';
        case 'pt':
          return 'pt_PT';
        case 'hu':
          return 'hu_HU';
        case 'it':
          return 'it_IT';
        case 'ru':
          return 'ru_RU';
        case 'tr':
          return 'tr_TR';
        case 'sv':
          return 'sv_SE';
        case 'pl':
          return 'pl_PL';
        case 'nb':
          return 'nb_NO';
        case 'da':
          return 'da_DK';
        default:
          return 'en_US';
      }
    },
    ogImage() {
      const languageSuffix = ['en', 'ja', 'ko', 'zh-hant'].includes(this.$i18n.locale) ? this.$i18n.locale : 'en';

      if (this.$route.name.match('free')) {
        return `https://static.moele.me/img/free/free-elements-og-${languageSuffix}.220830.jpg`;
      }

      return _.get(this.product, 'thumbnails.jpg.url') || this.getImgixOgImageUrl(this.image || `https://static.moele.me/img/motionelements-og-${languageSuffix}.220830.jpg`);
    },
    ogImageLang() {
      switch (this.$i18n.locale) {
        case 'ja':
        case 'ko':
        case 'zh-hant':
          return this.$i18n.locale;
        default:
          return 'en';
      }
    },
    normalizedPath() {
      return this.$link(this.$route.path, 'en').fullPath();
    },
    canonicalUrl() {
      // const path = this.normalizedPath;
      const path = this.$route.path;
      return `https://www.motionelements.com${path}`;
    },
    sampleImageUrl() {
      // use sample image url for imageJson
      // image not indexed yet
      if (isProductType(['image', 'video', 'video_template'], this.mediaType)) {
        return _.get(this.product, 'sample.image.url') || _.get(this.product, 'previews.jpg.url');

        // if (Array.isArray(samples)) {
        //   const sampleImage = samples.find(x => x.format === 'jpg');
        //   if (sampleImage) {
        //     return sampleImage.url;
        //   }
        // }
      }

      return _.get(this.product, 'previews.jpg.url');
    },
    videoJsonLd() {
      try {
        const videoUrl = _.get(this.product, 'previews.mp4.url');
        const sku = this.product.id;
        const name = this.jsonLdProductName();
        const description = this.jsonLdProductDescription(name);
        const duration = _.get(this.product, 'mediaTypeDetails.duration');
        const uploadDate = _.get(this.product, 'publishedAt');
        const productUrl = _.get(this.product, 'html.url');

        // const thumbnailUrls = _.get(this.product, 'thumbnails.jpgs', []).map(o => o.url);

        if (videoUrl && name) {
          const jsonLd = {
            '@context': 'http://schema.org',
            '@type': 'VideoObject',
            'name': truncateString(name, 80, ''), // $element->getSeoTitle(80, LANGUAGE_CODE),
            'description': stripHtml(description),
            'thumbnailUrl': _.get(this.product, 'thumbnails.jpg.url'), // use only 1
            // 'thumbnailUrl': thumbnailUrls || [_.get(this.product, 'thumbnails.jpg.url')],
            'publisher': {
              '@type': 'Organization',
              'name': 'MotionElements',
              'url': 'https://www.motionelements.com',
              'logo': 'https://static.moele.me/img/logo/MotionElements-logomark-square-512x512.png',
            },
            'contentUrl': videoUrl,
            'embedUrl': `https://www.motionelements.com/embed/c/${sku}`,
            'acquireLicensePage': productUrl,
            'author': {
              '@type': 'Person',
              'name': this.contributorName,
            },
            'encodingFormat': 'video/mp4',
            'copyrightHolder': {
              '@type': 'Thing',
              'name': `${this.contributorName} / MotionElements`,
            },
            'copyrightNotice': this.contributorName,
            'creditText': 'MotionElements',
            'creator': {
              '@type': 'Person',
              'name': this.contributorName,
            },
            'license': `https://www.motionelements.com/license?sku=${sku}`,
            'sourceOrganization': {
              '@type': 'Organization',
              'name': 'MotionElements.com',
            },
            //  "interactionCount": "2347"
          };

          if (uploadDate) {
            jsonLd.uploadDate = formatISO(fromUnixTime(uploadDate)); // $date->toISO8601String(),
          }

          if (duration) {
            jsonLd.duration = this.$tools.secondToIso8601Duration(duration);
          }

          return {
            type: 'application/ld+json',
            json: jsonLd,
          };
        }
      } catch (e) {
        console.error(e);
        // eat error
      }
      return null;
    },
    imageJsonLd() {
      // image not indexed yet
      if (!isProductType('image', this.mediaType)
        && !isProductType('video', this.mediaType)
        && !isProductType('video_template', this.mediaType)
      ) {
        return null;
      }

      try {
        // const imageUrl = _.get(this.product, 'previews.jpg.url');
        const imageUrl = this.sampleImageUrl;

        const sku = this.product.id;
        const name = this.jsonLdProductName();
        const description = this.jsonLdProductDescription(name);
        // const uploadDate = _.get(this.product, 'publishedAt');
        const productUrl = _.get(this.product, 'html.url');

        // https://developers.google.com/search/docs/data-types/image-license-metadata
        if (imageUrl && name) {
          const jsonLd = {
            '@context': 'http://schema.org',
            '@type': 'ImageObject',
            'name': truncateString(name, 80, ''), // $element->getSeoTitle(80, LANGUAGE_CODE),
            'description': stripHtml(description),
            'contentUrl': imageUrl,
            // use large image for thumbnailUrl
            // 'thumbnailUrl': imageUrl, // _.get(this.product, 'thumbnails.jpg.url'),  // use large image for thumbnailUrl
            'thumbnailUrl': _.get(this.product, 'thumbnails.jpg.url') || imageUrl, // test using thumbnail url
            'url': productUrl,
            'acquireLicensePage': productUrl,
            'author': {
              '@type': 'Person',
              'name': this.contributorName,
            },
            'encodingFormat': 'image/jpeg',
            'copyrightHolder': {
              '@type': 'Thing',
              'name': `${this.contributorName} / MotionElements`,
            },
            'copyrightNotice': this.contributorName,
            'creditText': 'MotionElements',
            'creator': {
              '@type': 'Person',
              'name': this.contributorName,
            },
            'license': `https://www.motionelements.com/license?sku=${sku}`,
            // 'publisher': {
            //   '@type': 'Organization',
            //   'name': 'MotionElements',
            //   'url': 'https://www.motionelements.com',
            //   'logo': 'https://static.moele.me/img/logo/MotionElements-logomark-square-512x512.png',
            // },
            'sourceOrganization': {
              '@type': 'Organization',
              'name': 'MotionElements.com',
            },

            // Conditionally add properties if they have values
            ...(this.width && this.height && { width: this.width, height: this.height }),
          };

          return {
            type: 'application/ld+json',
            json: jsonLd,
          };
        }
      } catch (e) {
        console.error(e);
        // eat error
      }
      return null;
    },
    breadcrumbJsonLd() {
      if (!_.size(this.breadcrumbs)) {
        return null;
      }

      const list = [];

      let position = 1;

      this.breadcrumbs.forEach((item) => {
        list.push({
          '@type': 'ListItem',
          'position': position,
          'item':
              {
                '@id': this.$link(item.link).url(),
                // use nameSeo if exists for SEO
                'name': _.get(item, 'nameSeo', item.name),
              },
        });

        position += 1;
      });

      return {
        type: 'application/ld+json',
        json: {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': list,
        },
      };
    },
    productJsonLd() {
      // const images = _.get(this.product, 'thumbnails.jpgs', []).map(thumbnail => ({
      //   '@type': 'ImageObject',
      //   'url': thumbnail.url,
      //   'contentUrl': thumbnail.url,
      // }));

      const categories = [
        `/${this.product.mediaType}`,
        ..._.get(this.product, 'categories', []).map(category => `/${this.product.mediaType}?category=${category.id}`),
      ];

      const sizes = _.get(this.product, 'variants', [])
        .filter(variant => variant.width && variant.height)
        .map(variant => `${variant.width} x ${variant.height}`);

      const jsonLd = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        'name': _.get(this.product, 'name', ''),
        'brand': _.get(this.product, 'artist.username', ''),
        'description': _.get(this.product, 'html.description', ''),
        'image': [
          _.get(this.product, 'previews.jpg.url', _.get(this.product, 'thumbnails.jpg.url', '')),
        ],
        'offers': {
          '@type': 'Offer',
          'url': _.get(this.product, 'html.url', ''),
          'price': _.get(this.product, 'price'),
          'priceCurrency': _.get(this.product, 'currency'),
          'availability': 'https://schema.org/InStock',
        },
        'category': categories,
        'keywords': _.get(this.product, 'keywords'),
        'size': sizes,
        // 'additionalProperty': [
        //   'Code 18228490',
        //   'Type After Effects template',
        //   'File Size 9.9 MB',
        //   'Duration 00:05',
        //   'Required Software After Effects & above',
        //   'Universalized No',
        //   'License Royalty-Free',
        //   'Music Not Included',
        //   'Sound Effects Not Included',
        //   'Stock Footage Not Included',
        //   'Text Placeholders 1',
        //   'Logo Placeholders 1',
        // ],
      };

      return {
        type: 'application/ld+json',
        json: jsonLd,
      };
    },
    pageJsonLds() {
      const scripts = [];

      if (this.breadcrumbJsonLd) {
        scripts.push(this.breadcrumbJsonLd);
      }

      if (this.videoJsonLd) {
        scripts.push(this.videoJsonLd);
      }

      if (this.imageJsonLd) {
        scripts.push(this.imageJsonLd);
      }

      if (this.productJsonLd) {
        scripts.push(this.productJsonLd);
      }

      return scripts;
    },
  },
  methods: {
    jsonLdProductName() {
      const name = _.get(this, 'product.name');
      if (typeof name === 'string') {
        return name || '';
      }
      if (name != null && name !== '') {
        if (this.$rollbar) {
          this.$rollbar.info('ProductPageSchema: product.name is not a string', {
            productId: _.get(this.product, 'id'),
            nameType: typeof name,
          });
        }
      }
      return '';
    },
    jsonLdProductDescription(fallbackName) {
      const description = _.get(this, 'product.description');
      if (typeof description === 'string') {
        return description || fallbackName;
      }
      if (description != null && description !== '') {
        if (this.$rollbar) {
          this.$rollbar.info('ProductPageSchema: product.description is not a string', {
            productId: _.get(this.product, 'id'),
            descriptionType: typeof description,
          });
        }
      }
      return fallbackName;
    },
    getOgImageLang() {
      switch (this.$i18n.locale) {
        case 'ja':
        case 'ko':
        case 'zh-hant':
          return this.$i18n.locale;
        default:
          return 'en';
      }
    },
    getPageLangLink() {
      // if (this.$route.name.match('home')) {
      //   return [
      //     { rel: 'canonical', href: `https://www.motionelements.com/${this.$i18n.locale === 'en' ? '' : `${this.$i18n.locale}/`}` },
      //     { rel: 'alternate', hreflang: 'x-default', href: 'https://www.motionelements.com/' },
      //     { rel: 'alternate', hreflang: 'en', href: 'https://www.motionelements.com/' },
      //     { rel: 'alternate', hreflang: 'ja', href: 'https://www.motionelements.com/ja/' },
      //     { rel: 'alternate', hreflang: 'ko', href: 'https://www.motionelements.com/ko/' },
      //     { rel: 'alternate', hreflang: 'zh-hant', href: 'https://www.motionelements.com/zh-hant/' },
      //     { rel: 'alternate', hreflang: 'th', href: 'https://www.motionelements.com/th/' },
      //     { rel: 'alternate', hreflang: 'de', href: 'https://www.motionelements.com/de/' },
      //     { rel: 'alternate', hreflang: 'es', href: 'https://www.motionelements.com/es/' },
      //     { rel: 'alternate', hreflang: 'fr', href: 'https://www.motionelements.com/fr/' },
      //     { rel: 'alternate', hreflang: 'pt', href: 'https://www.motionelements.com/pt/' },
      //   ];
      // }

      // languageCodes without en
      const languageCodes = process.env.VUE_APP_LANGUAGE_CODES ? process.env.VUE_APP_LANGUAGE_CODES.replace('en,', '').split(',') : [];

      const path = this.normalizedPath;

      const data = [
        { rel: 'canonical', href: this.canonicalUrl },
        { rel: 'alternate', hreflang: 'x-default', href: `https://www.motionelements.com${path}` },
        { rel: 'alternate', hreflang: 'en', href: `https://www.motionelements.com${path}` },
      ];

      languageCodes.forEach((languageCode) => {
        // convert to hrefLang code
        const hrefLanguageCode = languageCode === 'zh-hant' ? 'zh-Hant-TW' : languageCode;

        data.push({
          rel: 'alternate', hreflang: hrefLanguageCode, href: `https://www.motionelements.com/${languageCode}${path}`,
        });
      });
      return data;
    },
    getBreadcrumbJsonLd() {
      if (this.mediatype.match('free')) {
        return this.getFreeBreadcrumbJsonLd();
      }
      return {
        type: 'application/ld+json',
        json: {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement':
            [
              {
                '@type': 'ListItem',
                'position': 1,
                'item':
                  {
                    '@id': `${process.env.VUE_APP_SITE_URL}${this.$route.path}`,
                    'name': ['dlp', 'other'].indexOf(this.mediatype) > -1 ? this.title : this.getPageName(),
                  },
              },
            ],
        },
      };
    },
    getFreeBreadcrumbJsonLd() {
      return {
        type: 'application/ld+json',
        json: {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement':
            [
              {
                '@type': 'ListItem',
                'position': 1,
                'item':
                  {
                    '@id': this.$link(`/${this.getFreePageUpper(this.mediatype)}`).url(),
                    'name': this.$t(`header.${this.getFreePageUpper(this.mediatype)}`),
                  },
              },
              {
                '@type': 'ListItem',
                'position': 2,
                'item':
                  {
                    '@id': `${process.env.VUE_APP_SITE_URL}${this.$route.path}`,
                    'name': this.getPageName(),
                  },
              },
            ],
        },
      };
    },
    getPageName() {
      if (this.mediatype && this.mediatype.match('free')) {
        return this.$t(`${this.mediatype}.page_title`);
        // return this.$term('mediaType', this.mediatype).text();
      }
      return this.$t(`header.${this.mediatype.replace('_template', '')}`);
    },
    getFreePageUpper(mt) {
      switch (mt) {
        case 'free_pr':
          return 'pr';
        case 'free_ae':
          return 'ae';
        case 'free_fcp':
          return 'motion';
        case 'free_video':
          return 'video';
        case 'free_gif':
          return 'image';
        case 'free_music':
          return 'music';
        case 'free_sfx':
          return 'sfx';
        default:
          return 'video';
      }
    },
    //
    getImgixOgImageUrl(imageUrl) {
      // ?auto=format&fit=crop&w=1200&h=630'
      const url = new URL(imageUrl);
      url.searchParams.set('auto', 'format');
      url.searchParams.set('fit', 'crop');
      url.searchParams.set('w', 1200);
      url.searchParams.set('h', 630);
      return (
        `https://${
          url.hostname
        }${url.pathname
        }${url.search
        }`
      );
    },
  },
};
